
import React from 'react';
import {DashboardContainer} from "../../funcs/utils";
import {dollarToString} from "../../../lib/currency-utils";
import {DashboardTable} from "../dashboard-table";
import {sprintf} from "sprintf-js";
import { Bar } from "react-chartjs-2";

import {CategoryScale, Chart, LinearScale, BarElement} from 'chart.js';
Chart.register(CategoryScale, LinearScale, BarElement);

const STYLE_LUT = [
  {
    pred: (row, col) => true,
    style: {
      padding: "0 0.5rem",
      border: "solid black 1px",
    }
  }
];

const flatten_events = corp_events => {
  let events = [];
  Object.keys(corp_events).forEach(cusip => {
    Object.keys(corp_events[cusip]).forEach(ev_type => {
      events = events.concat(
        corp_events[cusip][ev_type].closed_items,
        corp_events[cusip][ev_type].pending_items);
    })
  });
  const cusips = {};
  events.forEach(event => {
    cusips[event.cusip] = event.size;
  })
  return {
    events,
    cusips,
  }
}

const calcWeightedTerms = (total_size, cusip_size_lut, cusip_metadata) => {
  let avgTermMonths = 0, avgTermYears = 0, avgCoupon = 0;

  const cusips = Object.keys(cusip_size_lut);
  for(let i=0; i < cusips.length; i++) {
    const curr_weight = cusip_size_lut[cusips[i]] / total_size;
    avgTermMonths += parseFloat(cusip_metadata[cusips[i]]['Term in Months']) * curr_weight;
    avgCoupon += parseFloat(cusip_metadata[cusips[i]]['Coupon']) * curr_weight;
  }
  avgTermYears = avgTermMonths / 12.;
  return { avgTermMonths, avgTermYears, avgCoupon };
}

/**
 * Linear interpolation coupon pct
 */
const calc_interp = (term_years, ust) => {
  const rates = ust.rates;
  if (term_years <= rates[0].tenor_yr) {
    return rates[0].rate_pct;
  }
  if (term_years >= rates[rates.length - 1].tenor_yr) {
    return rates[rates.length - 1].rate_pct;
  }
  for(let i=0; i < rates.length - 1; i++) {
    if (term_years >= rates[i].tenor_yr && term_years <= rates[i+1].tenor_yr) {
      const t = (term_years - rates[i].tenor_yr) / (rates[i+1].tenor_yr - rates[i].tenor_yr);
      return rates[i].rate_pct * (1-t) + rates[i+1].rate_pct * t;
    }
  }
  // Should not be here
  throw new Error("error");
}

const dump_annual_chart = events => {
  // We only care about PENDING_DIVIDEND events
  events = events.filter(ev => ev.partition === "PENDING_DIVIDEND");
  let tally = {};
  for(let i=0; i < events.length; i++) {
    const year = events[i].date.split("-")[0];
    if (!(year in tally)) {
      tally[year] = 0.;
    }
    tally[year] += events[i].amt_usd * events[i].size;
  }
  let labels = Object.keys(tally).sort();
  let data = labels.map(year => tally[year]);

  if (data.length === 1) {
    // Lets to monthly chart
    tally = {};
    for(let i=1; i <= 12; i++) {
      tally[sprintf("%02d", i)] = 0.;
    }
    for(let i=0; i < events.length; i++) {
      const month = events[i].date.split("-")[1];
      tally[month] += events[i].amt_usd * events[i].size;
    }
    labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    data = Object.keys(tally).sort().map(key => tally[key]);
  }

  const chart_data = {
      datasets: [
        {
          label: 'Annual Projected Portfolio Income',
          data,
          borderColor: '#212529',
          backgroundColor: '#212529',
        }
      ],
    labels,
    };

  const options = {
    scales: {
      y: {
        ticks: {
          callback: function(value, index, values) {
            return  Math.round(value).toLocaleString(
              'en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 });
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      }
    }
  }

  return (<Bar
    options={options}
    data={chart_data}/>)
}

const dump_characteristics_table = (corp_events, cusips, cash, cusip_metadata, ust) => {

  const notional_amt = Object.values(cusips).reduce((a, b) => a+b, 0);
  const total_holdings = Object.keys(cusips).length;
  const {  avgTermMonths, avgTermYears, avgCoupon } = calcWeightedTerms(notional_amt, cusips, cusip_metadata);
  const interpUSTCoupon = calc_interp(avgTermYears, ust);
  const spread_pct = avgCoupon - interpUSTCoupon;
  const performance_usd = spread_pct / 100. * notional_amt * 1000;
  const items = [
    [ "Notional amount put into CDs", dollarToString(notional_amt * 1000, true) ],
    [ "Number of Holdings", total_holdings ],
    [ "Cash", dollarToString(cash) ],
    [ "Weighted Average Maturity (YTW) of the Portfolio in Years", sprintf("%.2f", avgTermYears) ],
    [ "Weighted Average Maturity of the Portfolio in Months", sprintf("%.2f", avgTermMonths) ],
    [ "Weighted Average Coupon of the Portfolio", sprintf("%.2f%%", avgCoupon) ],
    [ "Equivilent Interpolated Tenor U.S. Yield Curve", sprintf("%.2f%%", interpUSTCoupon) ],
    [ "Spread to UST Yield", sprintf("%.2f%%", avgCoupon - interpUSTCoupon) ],
    [ "Estimated Over/Under Performance Relative to USTs", dollarToString(performance_usd, true)],
  ];

  const table_style = {
    width: "100%",
  }

  return (
    <DashboardTable
      col_widths={[3, 1]}
      tableStyle={table_style}
      tdStyleLut={STYLE_LUT}
      data={items}
      />
  )
}

const CouponStats = props => {
  const left_style = {
    marginTop: "0",
    marginRight: "1rem",
  }
  const right_style = {
    marginTop: "0",
    marginLeft: "1rem",
  }
  const ust = props.ust;
  const corp_events = props.corp_events;
  const { events, cusips } = flatten_events(corp_events);
  return (
    <div style={{display: "flex"}}>
      <div style={{flex: "1"}}>
        <DashboardContainer
          div_style={left_style}
          client={props.client}
          title={"Portfolio Characteristics"}>
          { dump_characteristics_table(
            corp_events, cusips, props.cash, props.cusip_metadata, ust)}
        </DashboardContainer>
      </div>
      <div style={{flex: "1"}}>
        <DashboardContainer
          div_style={right_style}
          client={props.client}
          title={"Annual Projected Portfolio Income"}>
          { dump_annual_chart(events) }
        </DashboardContainer>
      </div>
    </div>

  )

}

export {CouponStats};
